/**
 * AAA-01: the VERIFY / CONTRADICTION verb — the differentiator's logic core. Pure,
 * deterministic, engine-agnostic (no Three.js, no DOM, no RNG, no wall-clock — the
 * netcode-replay invariant). The client emits a 'VERIFIED' ObjectiveEvent when the
 * player performs the [V] verify verb at a memorial/derelict signal; this module
 * decides the carved NAME shown in the Verification Ledger and whether the record
 * is VERIFIED or a CONTRADICTION (a name carved as dead whose drift-log says the
 * subject is still alive — the hook: "verify one name... of a man who's still alive").
 *
 * The contradiction is content-driven and fully deterministic by the signal's seed
 * (see signal.ts carvedRecordFor): e.g. the HALIS VORN cutter's log — "tell them
 * HALIS VORN is not dead" — flips the record to CONTRADICTION.
 */
import { carvedRecordFor, type SignalSource } from './signal.js';

/** The three ledger states (mirrors the design-system color grammar). */
export type VerifyStatus = 'PENDING' | 'VERIFIED' | 'CONTRADICTION';

/** The outcome of verifying a signal's carved record. */
export interface VerifyResult {
  name: string; // the carved name (bare, ledger-ready, all-caps)
  status: VerifyStatus;
}

/** Signal kinds that present a record worth verifying (others are PENDING). */
const VERIFIABLE = new Set(['derelict', 'distress', 'memorial', 'anomaly']);

/** Phrases in a drift-log that assert a carved-dead subject is in fact ALIVE. */
const ALIVE_CLAIM = /\b(is not dead|not dead|still alive|did not die|not deceased|still here)\b/i;

/**
 * Pure predicate: does this carved record's drift-log CONTRADICT the death record?
 * True when the log asserts the subject is still alive. Deterministic (regex over
 * authored, seed-selected lore — no clocks, no RNG).
 */
export const contradicts = (record: { name: string; log: string | null }): boolean =>
  record.log !== null && ALIVE_CLAIM.test(record.log);

/** Strip the "— descriptor" suffix to the bare carved name, upper-cased for the wall. */
const bareName = (title: string): string => (title.split('—')[0] ?? title).trim().toUpperCase();

/**
 * Verify a signal's carved record. Pure + deterministic by the signal's seed/kind:
 * the same signal always yields the same name + status. Non-verifiable kinds
 * (wreckfield/mining/ambush) return PENDING with their generic title.
 */
export const verifySignal = (sig: SignalSource): VerifyResult => {
  const record = carvedRecordFor(sig);
  const name = bareName(record.name);
  if (!VERIFIABLE.has(sig.kind)) return { name, status: 'PENDING' };
  return { name, status: contradicts(record) ? 'CONTRADICTION' : 'VERIFIED' };
};

/**
 * Map a signal to the ledger ROW the HUD appends on verify. Identical determination
 * as verifySignal; named separately as the HUD-facing entry point (the client adds
 * this row to the Verification Ledger and reveals it glyph-by-glyph).
 */
export const ledgerEntryFor = (sig: SignalSource): VerifyResult => verifySignal(sig);
